/**
 * Позначки в браузері.
 *
 * Правки застосовуються одразу (оптимістично), а на сервер ідуть пачками —
 * малювання пальцем по таблиці не має чекати на мережу. Історія тримає зворотні
 * операції, тому «скасувати» працює і після синхронізації з сервером.
 */

import {
  cellKey,
  MarkIndex,
  type IsoDate,
  type Mark,
  type MarkOp,
  type MarksResponse,
  type ServerEvent,
} from '@kancelaria/shared';
import { create } from 'zustand';
import { api, ApiFailure, errorText } from '../api/client.js';
import { lockCheck } from './lock.js';
import { useLink } from './link.js';
import { toast } from './ui.js';

const CLIENT_ID = Math.random().toString(36).slice(2);
const HISTORY_LIMIT = 100;

interface HistoryEntry {
  redo: MarkOp[];
  undo: MarkOp[];
}

interface MarksState {
  index: MarkIndex;
  /** Лічильник змін — компоненти підписуються на нього замість глибокого порівняння. */
  version: number;
  /**
   * Той самий лічильник, але з паузою після останньої правки.
   *
   * Клітинка під пальцем має мінятися миттєво, а от підсумки внизу таблиці й
   * підсвітка «у кого нарядів найменше» перебирають усіх людей за всі дні —
   * рахувати це на кожну клітинку протяжки означало б рахувати сотню разів
   * поспіль і викидати результат. Тому вони чекають, поки рука зупиниться.
   */
  settled: number;
  /**
   * Версія кожного рядка окремо: під час малювання перемальовується лише той
   * рядок, якого торкнулися, а не вся таблиця на кілька тисяч клітинок.
   */
  rowVersions: Map<string, number>;
  from: IsoDate;
  to: IsoDate;
  loading: boolean;
  syncing: boolean;
  online: boolean;
  history: HistoryEntry[];
  future: HistoryEntry[];
  load: (from: IsoDate, to: IsoDate) => Promise<void>;
  reload: () => Promise<void>;
  apply: (ops: MarkOp[], opts?: { history?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  /** Чужа правка з каналу подій. */
  applyRemote: (event: Extract<ServerEvent, { type: 'marks' }>) => void;
  flush: () => Promise<void>;
}

/* -------------------------------------------------- черга відправки на сервер */

const QUEUE_KEY = 'kanc.marks.queue';
const SNAP_KEY = 'kanc.marks.snap';

let queue: MarkOp[] = [];
/** Уже полетіли на сервер, але відповіді ще немає. */
let sending: MarkOp[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<void> = Promise.resolve();

function readOps(): MarkOp[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MarkOp[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistQueue(): void {
  if (queueTimer) {
    clearTimeout(queueTimer);
    queueTimer = null;
  }
  const pending = dedupe(queue);
  try {
    if (pending.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(pending));
    else localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* квота */
  }
  useLink.getState().setMarksPending(pending.length);
}

/**
 * Запис черги в `localStorage` відкладаємо.
 *
 * Черга переживає закриття вкладки, тож писати її треба — але робити це на
 * кожну клітинку протяжки означає щоразу серіалізувати весь список: під час
 * заливки місяця це тисячі елементів на кожен рух пальця. Пауза нічого не
 * коштує: перед виходом зі сторінки й перед відправкою черга дописується
 * повністю.
 */
let queueTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePersistQueue(): void {
  if (queueTimer) return;
  queueTimer = setTimeout(() => {
    queueTimer = null;
    persistQueue();
  }, 300);
}

interface MarksSnap {
  from: string;
  to: string;
  marks: Array<{ personId: string; date: string; meal: number; kind: string; layer?: string }>;
}

function persistSnap(from: string, to: string, index: MarkIndex): void {
  if (!from || !to) return;
  try {
    const snap: MarksSnap = { from, to, marks: [...index.all()] };
    localStorage.setItem(SNAP_KEY, JSON.stringify(snap));
  } catch {
    /* квота */
  }
}

function readSnap(from: string, to: string): MarksSnap | null {
  try {
    const raw = localStorage.getItem(SNAP_KEY);
    if (!raw) return null;
    const snap = JSON.parse(raw) as MarksSnap;
    return snap.from === from && snap.to === to ? snap : null;
  } catch {
    return null;
  }
}

queue = readOps();

/**
 * Попередження про замок. Протяжка шле правку на кожну клітинку, тож без
 * паузи користувач отримав би десяток однакових сповіщень поспіль.
 */
let warnedAt = 0;
function warnLocked(): void {
  const now = Date.now();
  if (now - warnedAt < 3000) return;
  warnedAt = now;
  toast('День закритий. Щоб правити — зніміть замок у панелі або в шапці дня', 'err');
}

const keyOf = (op: MarkOp): string =>
  `${op.personId}|${cellKey(op.date, op.meal)}|${op.layer ?? 'main'}`;

const dedupe = (ops: MarkOp[]): MarkOp[] => {
  const map = new Map<string, MarkOp>();
  for (const op of ops) map.set(keyOf(op), op);
  return [...map.values()];
};

persistQueue();

function scheduleFlush(set: (p: Partial<MarksState>) => void): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void flushNow(set), 250);
}

/**
 * Дати підсумкам знати, що правки скінчилися. Кожна нова правка відсуває
 * момент: поки триває протяжка, важкі підрахунки не запускаються жодного разу.
 */
let settleTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSettle(set: (p: Partial<MarksState>) => void): void {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    settleTimer = null;
    set({ settled: useMarks.getState().version });
  }, 140);
}

let snapTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSnap(): void {
  if (snapTimer) clearTimeout(snapTimer);
  snapTimer = setTimeout(() => {
    const { from, to, index } = useMarks.getState();
    persistSnap(from, to, index);
  }, 400);
}

async function flushNow(set: (p: Partial<MarksState>) => void): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!queue.length) return;
  if (!useLink.getState().online) {
    persistQueue();
    return;
  }
  const ops = dedupe(queue);
  queue = [];
  persistQueue();
  set({ syncing: true });
  inFlight = inFlight
    .then(() => {
      sending = ops;
      return api.post('/api/marks', { ops, clientId: CLIENT_ID });
    })
    .then(() => {
      sending = [];
      set({ syncing: false, online: true });
      useLink.getState().setOnline(true);
      // Знімок теж відкладаємо: він серіалізує всі позначки місяця, а відправка
      // під час малювання відбувається кожні чверть секунди.
      scheduleSnap();
    })
    .catch((e: unknown) => {
      sending = [];
      const client =
        e instanceof ApiFailure && e.status >= 400 && e.status < 500 && e.status !== 401;
      if (!client) {
        queue = dedupe([...ops, ...queue]);
        useLink.getState().setOnline(false);
      } else {
        toast(`Зміни не збереглися: ${errorText(e)}`, 'err');
      }
      persistQueue();
      set({ syncing: false, online: false });
    });
  await inFlight;
}

/* ----------------------------------------------------------------- сховище */

/** Підняти версії лише зачеплених рядків. */
const bumpRows = (prev: Map<string, number>, ops: MarkOp[]): Map<string, number> => {
  const next = new Map(prev);
  for (const op of ops) next.set(op.personId, (next.get(op.personId) ?? 0) + 1);
  return next;
};

export const useMarks = create<MarksState>((set, get) => ({
  index: new MarkIndex(),
  version: 0,
  settled: 0,
  rowVersions: new Map(),
  from: '',
  to: '',
  loading: false,
  syncing: false,
  online: true,
  history: [],
  future: [],

  load: async (from, to) => {
    set({ loading: true, from, to });
    try {
      const res = await api.get<MarksResponse>(`/api/marks?from=${from}&to=${to}`);
      const index = MarkIndex.from(res.marks);
      const pending = dedupe(queue);
      if (pending.length) index.applyAll(pending);
      persistSnap(from, to, index);
      set(s => ({
        index,
        version: s.version + 1,
        settled: s.version + 1,
        rowVersions: bumpRows(new Map(), [...res.marks, ...pending]),
        loading: false,
        online: true,
        history: [],
        future: [],
      }));
      useLink.getState().setOnline(true);
      if (pending.length) scheduleFlush(set);
    } catch (e) {
      const snap = readSnap(from, to);
      if (snap) {
        const index = MarkIndex.from(snap.marks as Mark[]);
        set(s => ({
          index,
          version: s.version + 1,
          settled: s.version + 1,
          rowVersions: bumpRows(new Map(), snap.marks as Mark[]),
          loading: false,
          online: false,
          history: [],
          future: [],
        }));
        useLink.getState().setOnline(false);
        return;
      }
      set({ loading: false, online: false });
      toast(errorText(e), 'err');
    }
  },

  reload: async () => {
    const { from, to } = get();
    if (from && to) await get().load(from, to);
  },

  apply: (ops, opts = {}) => {
    if (!ops.length) return;
    // Замок минулих днів стоїть тут, а не на кожній кнопці: через `apply`
    // проходять і клік, і протяжка, і заливка стовпця, і підказки.
    // Перевірку замків збираємо один раз на пачку: заливка місяця — це десятки
    // тисяч операцій, і питати поточну дату для кожної було чистою витратою.
    const locked = lockCheck();
    const open = ops.filter(op => !locked(op.date));
    if (open.length < ops.length) warnLocked();
    if (!open.length) return;
    const { index } = get();
    const undo: MarkOp[] = open.map(op => ({
      personId: op.personId,
      date: op.date,
      meal: op.meal,
      layer: op.layer ?? 'main',
      kind: index.get(op.personId, op.date, op.meal, op.layer ?? 'main'),
    }));
    index.applyAll(open);
    queue.push(...open);
    schedulePersistQueue();
    scheduleSnap();
    scheduleSettle(set);
    set(s => ({
      version: s.version + 1,
      rowVersions: bumpRows(s.rowVersions, open),
      history:
        opts.history === false
          ? s.history
          : [...s.history, { redo: open, undo }].slice(-HISTORY_LIMIT),
      future: opts.history === false ? s.future : [],
    }));
    scheduleFlush(set);
  },

  undo: () => {
    const { history, index } = get();
    const last = history[history.length - 1];
    if (!last) return;
    index.applyAll(last.undo);
    queue.push(...last.undo);
    schedulePersistQueue();
    scheduleSnap();
    scheduleSettle(set);
    set(s => ({
      version: s.version + 1,
      rowVersions: bumpRows(s.rowVersions, last.undo),
      history: s.history.slice(0, -1),
      future: [...s.future, last],
    }));
    scheduleFlush(set);
  },

  redo: () => {
    const { future, index } = get();
    const next = future[future.length - 1];
    if (!next) return;
    index.applyAll(next.redo);
    queue.push(...next.redo);
    schedulePersistQueue();
    scheduleSnap();
    scheduleSettle(set);
    set(s => ({
      version: s.version + 1,
      rowVersions: bumpRows(s.rowVersions, next.redo),
      future: s.future.slice(0, -1),
      history: [...s.history, next],
    }));
    scheduleFlush(set);
  },

  flush: () => flushNow(set),

  /**
   * Живі зміни від інших користувачів.
   *
   * Клітинки, які ми самі щойно поставили і які ще не дійшли до сервера
   * (у черзі або в дорозі), чужою правкою не чіпаємо: наша піде на сервер
   * пізніше і стане останньою — інакше екран показував би чуже значення,
   * а сервер тримав наше.
   */
  applyRemote: data => {
    if (data.clientId === CLIENT_ID) return;
    const { index, from, to } = get();
    // Період ще не завантажено — нема що правити, дані прийдуть із load().
    if (!from || !to) return;
    if (!data.ops.length) {
      void get().reload();
      return;
    }
    const ours = new Set([...queue, ...sending].map(keyOf));
    const relevant = data.ops.filter(
      o => o.date >= from && o.date <= to && !ours.has(keyOf(o)),
    );
    if (!relevant.length) return;
    index.applyAll(relevant);
    set(s => ({ version: s.version + 1, rowVersions: bumpRows(s.rowVersions, relevant) }));
    scheduleSettle(set);
    scheduleSnap();
  },
}));

/** Перед закриттям вкладки дописуємо все, що не встигло полетіти. */
window.addEventListener('beforeunload', () => {
  persistQueue();
  const { from, to, index } = useMarks.getState();
  persistSnap(from, to, index);
  if (queue.length) void useMarks.getState().flush();
});
